import { ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useState } from 'react'
import { CartProvider } from '@/contexts/CartContext'
import { SessionTimeoutManager } from '@/components/session/SessionTimeoutManager'
import { TenantBrandingProvider } from '@/components/TenantBrandingProvider'

interface ProvidersProps {
  children: ReactNode
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes (increased from 1 minute)
            gcTime: 10 * 60 * 1000, // 10 minutes cache
            retry: (failureCount, error) => {
              // Don't retry on 4xx errors
              if (error && typeof error === 'object' && 'status' in error) {
                const status = error.status as number
                if (status >= 400 && status < 500) {
                  return false
                }
                if (status === 429) {
                  return false // Don't retry on rate limit
                }
              }
              return failureCount < 2 // Reduce retry attempts from 3 to 2
            },
            refetchOnWindowFocus: false, // Disable automatic refocus refetch
            refetchOnReconnect: true, // Keep reconnect refetch
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
          },
          mutations: {
            retry: 1, // Reduce mutation retries
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        <TenantBrandingProvider>
          {children}
          <SessionTimeoutManager />
        </TenantBrandingProvider>
      </CartProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
